/**
 * SEO Helper Utilities
 * Functions for optimizing meta tags and structured data
 */

/**
 * Truncate title to optimal length (≤ 60 chars)
 * Prioritizes keeping the most important part when truncating
 */
export function truncateTitle(title: string, maxLength: number = 60): string {
  if (title.length <= maxLength) return title;
  
  // If there's a separator (– or |), try to keep the main part
  const separators = [' – ', ' | ', ' - '];
  for (const sep of separators) {
    if (title.includes(sep)) {
      const parts = title.split(sep);
      // Keep the first part if it fits
      if (parts[0].length <= maxLength) {
        return parts[0].trim();
      }
    }
  }
  
  // Otherwise, truncate and add ellipsis
  return title.substring(0, maxLength - 1).trim() + '…';
}

/**
 * Truncate description to optimal length (≤ 155 chars)
 * Tries to end at a complete sentence or word boundary
 */
export function truncateDescription(description: string, maxLength: number = 155): string {
  if (description.length <= maxLength) return description;
  
  // Try to find the last sentence ending before maxLength
  const sentenceEndings = ['. ', '! ', '? '];
  let lastSentenceEnd = -1;
  
  for (const ending of sentenceEndings) {
    const pos = description.lastIndexOf(ending, maxLength);
    if (pos > lastSentenceEnd) {
      lastSentenceEnd = pos;
    }
  }
  
  // If we found a sentence ending, use it
  if (lastSentenceEnd > maxLength * 0.7) { // At least 70% of maxLength
    return description.substring(0, lastSentenceEnd + 1).trim();
  }
  
  // Otherwise, try to end at a word boundary
  const lastSpace = description.lastIndexOf(' ', maxLength);
  if (lastSpace > maxLength * 0.8) { // At least 80% of maxLength
    return description.substring(0, lastSpace).trim() + '…';
  }
  
  // Last resort: hard truncate
  return description.substring(0, maxLength - 1).trim() + '…';
}

/**
 * Format card title for SEO
 * Format: "{CardName} – {Effektivzins}% Effektivzins, {Jahresgebühr}"
 */
export function formatCardTitle(
  cardName: string,
  effectiveRate?: number,
  annualFee?: number,
  maxLength: number = 60
): string {
  const rateText = typeof effectiveRate === 'number' ? `${effectiveRate.toFixed(2)}% Effektivzins` : undefined;
  let feeText: string | undefined;
  if (typeof annualFee === 'number') {
    feeText = annualFee === 0 ? 'keine Jahresgebühr' : `${annualFee.toLocaleString('de-DE')} € Jahresgebühr`;
  }
  const parts = [cardName, [rateText, feeText].filter(Boolean).join(', ')].filter(Boolean);
  const title = parts.join(' – ');
  return truncateTitle(title, maxLength);
}

/**
 * Format card description for SEO
 */
export function formatCardDescription(
  shortPitch: string,
  cardName: string,
  maxLength: number = 155
): string {
  const base = shortPitch?.trim()
    ? shortPitch.trim()
    : `Vergleichen Sie Zinsen, Vorteile und Gebühren für ${cardName}.`;
  const description = `${base} Alle Angaben regelmäßig geprüft.`;
  return truncateDescription(description, maxLength);
}

/**
 * Validate meta tag lengths
 */
export interface MetaValidation {
  isValid: boolean;
  warnings: string[];
}

export function validateMetaTags(title: string, description: string): MetaValidation {
  const warnings: string[] = [];
  
  // Title validation
  if (title.length > 60) {
    warnings.push(`Title too long (${title.length} chars, max 60)`);
  } else if (title.length < 30) {
    warnings.push(`Title too short (${title.length} chars, min 30 recommended)`);
  }
  
  // Description validation
  if (description.length > 155) {
    warnings.push(`Description too long (${description.length} chars, max 155)`);
  } else if (description.length < 100) {
    warnings.push(`Description too short (${description.length} chars, min 100 recommended)`);
  }
  
  return {
    isValid: warnings.length === 0,
    warnings
  };
}

/**
 * Strip HTML tags from text
 */
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Generate optimal OG image dimensions
 */
export const OG_IMAGE_DIMENSIONS = {
  width: 1200,
  height: 630,
  aspectRatio: '1200:630'
} as const;

/**
 * Extracts clean slug from various URL formats
 * Handles:
 * - Full URLs: https://kredittkort.com/skolen/kredittkort-med-betalingsanmerkning
 * - Relative paths: /artikler/kredittkort-med-betalingsanmerkning
 * - Clean slugs: kredittkort-med-betalingsanmerkning
 */
export function extractSlugFromUrl(input: string): string {
  if (!input) return '';
  
  let cleaned = input.trim();
  
  // Remove protocol (http://, https://)
  cleaned = cleaned.replace(/^https?:\/\//, '');
  
  // Remove domain (everything before first /)
  const domainEnd = cleaned.indexOf('/');
  if (domainEnd !== -1) {
    cleaned = cleaned.substring(domainEnd);
  }
  
  // Remove leading/trailing slashes
  cleaned = cleaned.replace(/^\/+|\/+$/g, '');
  
  // Remove known path prefixes
  cleaned = cleaned.replace(/^(ratgeber|kreditkarten|glossar)\//i, '');
  
  // Remove any remaining slashes (keep only last segment)
  const segments = cleaned.split('/');
  cleaned = segments[segments.length - 1];
  
  return cleaned;
}
